import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Subject, forkJoin, firstValueFrom, of } from "rxjs"; // Import forkJoin, firstValueFrom, of
import { takeUntil } from "rxjs/operators";
import { HttpErrorResponse } from '@angular/common/http';

import { ApiService, Region, Country } from "@core/services/api/api.service";
import { ManageAssessmentApiService, ClassificationsStatus, DeviceClassifications, ProductDesignHistory, ChangeTypes, ProductFamilies, ManageAssessmentRequestPayload } from "@core/services/api/manage-assessment-api.service";

import { PrimeNGSharedModule } from "@shared/prime/prime-ng.module";
import { MessageService } from "primeng/api";
import { CalendarModule } from 'primeng/calendar';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';

/** Read-only rich/plain fields that open the shared full-screen preview dialog */
type ReadonlyFullscreenField = "changeDescription" | "impactAssessJustify" | "timeProductNotify";

// Define the UploadedFile interface for consistency
interface UploadedFile {
  name: string;
  url: string;
  path?: string; // Optional, if you need to store the path
  id?: number; // Optional, if it's an existing attachment from the backend
  status?: string; // Add status to track attachment state
  uploadedAt?: string;
}

@Component({
  selector: "app-manage-assessment-detail-popup",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNGSharedModule,
    CalendarModule,
    MultiSelectModule,
    DropdownModule,
    EditorModule,
  ],
  templateUrl: "./manage-assessment-detail-popup.component.html",
  styleUrl: "./manage-assessment-detail-popup.component.scss",
  providers: [MessageService, DatePipe],
})
export class ManageAssessmentDetailPopupComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  @Input() mappingId: any = null; // This should contain rca_id and response_mapping_id
  @Input() historyId: number | null = null; // History ID for viewing specific version
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeDialog = new EventEmitter<void>();
  @Output() rcaPatched = new EventEmitter<void>();

  rcaForm!: FormGroup;
  formSubmitted: boolean = false;

  countries: Country[] = [];
  regions: Region[] = [];
  allCountries: Country[] = [];
  usMedDeviceOptions: DeviceClassifications[] = [];
  usClassifications: ClassificationsStatus[] = [];
  euMedDeviceOptions: DeviceClassifications[] = [];
  typeOfChanges: ChangeTypes[] = [];
  euClassifications: ClassificationsStatus[] = [];
  productFamilies: ProductFamilies[] = [];
  productDesignHistoryOptions: ProductDesignHistory[] = [];

  // New dropdown options from manage-response-next-form
  deviceClassificationsOptions: any[] = [
    { id: 1, name: 'Class A' }, { id: 2, name: 'Class B' }, { id: 3, name: 'Class C' },
    { id: 4, name: 'Class D' }, { id: 5, name: 'Class I' }, { id: 6, name: 'Class I (low risk) - MX Specific' },
    { id: 7, name: 'Class II' }, { id: 8, name: 'Class IIa' }, { id: 9, name: 'Class IIb' },
    { id: 10, name: 'Class III' }, { id: 11, name: 'Class Im' }, { id: 12, name: 'Class Ir' },
    { id: 13, name: 'Class Is' },
    { id: 14, name: 'N/A'},
    { id: 15, name: 'Non-Medical Device'},
  ];

  impactConclusionOptions: any[] = [
    { id: true, name: 'Yes' },
    { id: false, name: 'No' }
  ];

  yesNoOptions: any[] = [
    { id: true, name: 'Yes' },
    { id: false, name: 'No' }
  ];

  documentationOptions: any[] = [
    { id: "eudeclaration", name: "EU Declaration of Conformity" },
    { id: "cecertificate", name: "CE Certificate (Full Quality Assurance)" },
    { id: "labels", name: "Primary/Secondary/UDI labels" },
    { id: "ifu", name: "IFU/Operation Manual" },
    { id: "riskmanagement", name: "Risk Management Document" },
    { id: "udi", name: "UDI(GTIN/GLOBAN)" },
    { id: "iso13485", name: "ISO 13485 Certificate" },
    { id: "cfg", name: "CFG (Certificate of Foreign Government – U.S. document)" },
    { id: "coe", name: "COE (Certificate of Exportability – U.S. document)" },
    { id: "fsc", name: "FSC (Free Sales Certificate)" },
    { id: "eutechnicalfile", name: "EU Technical file" },
    { id: "510k", name: "510(k)" },
    { id: "vnv", name: "V&V Documentation (Performance, shelf life, etc.)" },
    { id: "msds", name: "MSDS" },
    { id: "others", name: "Other(s)" },
  ];

  raAdminUploadedFiles: UploadedFile[] = []; // Attachments uploaded by RA Admin
  responseUploadedFiles: UploadedFile[] = []; // Attachments uploaded via local RA response

  private unsubscribe$ = new Subject<void>();

  // Track whether "Other(s)" is selected in Product Design History Documentation
  showOtherDesignDocsField: boolean = false;
  fullAssessmentData: any; // This will hold the combined RCA and Response data

  /** Which read-only field is shown in the shared full-screen dialog; null = closed */
  readonlyExpanded: ReadonlyFullscreenField | null = null;

  readonly readonlyMaxDialogHeaders: Record<ReadonlyFullscreenField, string> = {
    changeDescription: "Change Description",
    impactAssessJustify: "Impact assessment and justification",
    timeProductNotify: "Acceptable duration for old and new products on the market",
  };

  readonly readonlyMaxDialogContentStyle: Record<string, string | number> = {
    overflow: "hidden",
    display: "flex",
    "flex-direction": "column",
    flex: "1 1 auto",
    "min-height": 0,
  };

  get readonlyMaxDialogVisible(): boolean {
    return this.readonlyExpanded !== null;
  }

  set readonlyMaxDialogVisible(visible: boolean) {
    if (!visible) {
      this.readonlyExpanded = null;
    }
  }

  // Quill editor configuration
  editorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  constructor(
    private fb: FormBuilder,
    private manageAssessmentApiService: ManageAssessmentApiService,
    private apiService: ApiService, // Inject ApiService for download
    private messageService: MessageService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // console.log('ManageAssessmentDetailPopup ngOnInit - mappingId:', this.mappingId);
    // console.log('ManageAssessmentDetailPopup ngOnInit - historyId:', this.historyId);
    this.initializeForm();
    this.loadDropdownData(); // Load all dropdowns first
    this.setupDependentDropdowns();
    // Fetch the combined RCA and Response data
    if (this.mappingId && this.mappingId.rca_id) {
      // console.log('Fetching assessment details for rca_id:', this.mappingId.rca_id);
      this.fetchAssessmentDetails();
    } else {
      console.warn('Cannot fetch assessment details - invalid mappingId structure:', this.mappingId);
    }
  }

  fetchAssessmentDetails() {
    // console.log('fetchAssessmentDetails called with mappingId:', this.mappingId, 'historyId:', this.historyId);
    // Determine which API call to use based on historyId
    const rcaDetailsObservable = this.historyId
      ? this.manageAssessmentApiService.getManageAssessmentByRcaHistory(this.mappingId.rca_id, this.historyId)
      : this.manageAssessmentApiService.getManageAssessmentByRca(this.mappingId.rca_id);

    // Use forkJoin to fetch both RCA details and Response form details in parallel
    forkJoin({
      rcaDetails: rcaDetailsObservable,
      responseForm: this.mappingId.response_mapping_id ?
        this.manageAssessmentApiService.getResponseRcaForm(this.mappingId.response_mapping_id) :
        // If no response_mapping_id, return an observable that immediately completes with null
        of(null)
    })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ({ rcaDetails, responseForm }) => {
          // console.log('Received RCA details:', rcaDetails);
          // console.log('Received response form:', responseForm);

          const rcaAttachments = Array.isArray(rcaDetails?.attachments)
            ? rcaDetails.attachments
            : [];
          const responseAttachments = Array.isArray(responseForm?.attachments)
            ? responseForm.attachments
            : [];
          const combinedAttachments = [...rcaAttachments, ...responseAttachments];

          // Combine all data into a single object
          this.fullAssessmentData = {
            ...this.mappingId, // Contains response_mapping_id, rca_id etc.
            ...rcaDetails, // Contains RCA-specific fields
            ...(responseForm || {}), // Contains response-specific fields (empty when no response)
            attachments: combinedAttachments,
            rcaAttachments,
            responseAttachments,
          };
          // console.log('Combined assessment data:', this.fullAssessmentData);
          // Ensure all dropdown data is loaded before patching
          this.loadAllDropdownsAndPatchForm(this.fullAssessmentData);
        },
        error: (err: HttpErrorResponse) => {
          console.error("Failed to fetch assessment details:", err);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to fetch assessment details. Please try again.",
            life: 5000,
          });
        }
      });
  }

  loadAllDropdownsAndPatchForm(dataToPatch: any) {
    Promise.all([
      this.apiService.getRegions().toPromise(),
      this.apiService.getCountries().toPromise(),
      this.manageAssessmentApiService.getClassifications().toPromise(),
      this.manageAssessmentApiService.getUsDeviceClassifications().toPromise(),
      this.manageAssessmentApiService.getEuDeviceClassifications().toPromise(),
      this.manageAssessmentApiService.getProductDesignHistoryDoc().toPromise(),
      this.manageAssessmentApiService.getChangeTypes().toPromise(),
      this.manageAssessmentApiService.getProductFamilies().toPromise(),
    ])
      .then(
        ([
          regionsResponse,
          countriesResponse,
          classificationsResponse,
          usDeviceClassificationsResponse,
          euDeviceClassificationsResponse,
          productDesignHistoryResponse,
          changeTypesResponse,
          productFamiliesResponse,
        ]) => {
          this.regions = regionsResponse?.regions || [];
          this.allCountries = countriesResponse?.countries || [];
          this.countries = [...this.allCountries]; // Initialize with all countries
          this.usClassifications =
            classificationsResponse?.classifications_status || [];
          this.euClassifications =
            classificationsResponse?.classifications_status || [];
          this.usMedDeviceOptions =
            usDeviceClassificationsResponse?.us_device_classifications || [];
          this.euMedDeviceOptions =
            euDeviceClassificationsResponse?.eu_device_classifications || [];
          this.productDesignHistoryOptions =
            productDesignHistoryResponse?.product_design_history_docs || [];
          this.typeOfChanges = changeTypesResponse?.change_types || [];
          this.productFamilies =
            productFamiliesResponse?.product_families || [];
          // Now that all dropdown data is loaded, patch the form
          this.patchFormWithRcaData(dataToPatch);
        }
      )
      .catch((error) => {
        console.error("Failed to load all dropdown data for patching:", error);
        this.messageService.add({
          severity: "error",
          summary: "Data Load Error",
          detail: "Failed to load necessary data for form. Please try again.",
          life: 5000,
        });
      });
  }

  setupDependentDropdowns(): void {
    const responsibleRegionControl = this.rcaForm.get("region");
    const responsibleCountryControl = this.rcaForm.get("country");

    responsibleRegionControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedRegions: Region[] | null) => {
        responsibleCountryControl?.reset([]); // Reset countries when regions change
        this.countries = []; // Clear current countries
        if (selectedRegions && selectedRegions.length > 0) {
          // Filter countries based on ALL selected regions
          const selectedRegionIds = selectedRegions.map(region => region.id);
          this.countries = this.allCountries.filter(
            (country) => selectedRegionIds.includes(country.region.id)
          );
          responsibleCountryControl?.enable();
        } else {
          // If no regions are selected, disable country and clear its options
          responsibleCountryControl?.disable();
          this.countries = [];
        }
      });
  }

  loadDropdownData(): void {
    // This method is now primarily for initial loading, but `loadAllDropdownsAndPatchForm`
    // will ensure they are fully loaded before patching.
    // You can keep this for initial UI rendering if needed, but the Promise.all
    // in `loadAllDropdownsAndPatchForm` is the critical part for patching.
    this.apiService
      .getRegions()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { regions: Region[] }) =>
          (this.regions = response.regions),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load regions", err),
      });

    this.apiService
      .getCountries()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { countries: Country[] }) => {
          this.allCountries = response.countries;
          this.countries = [...this.allCountries];
        },
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load countries", err),
      });

    this.manageAssessmentApiService
      .getClassifications()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { classifications_status: ClassificationsStatus[] }) =>
        (this.usClassifications = this.euClassifications =
          response.classifications_status),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load US/EU classifications", err),
      });

    this.manageAssessmentApiService
      .getUsDeviceClassifications()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: {
          us_device_classifications: DeviceClassifications[];
        }) => (this.usMedDeviceOptions = response.us_device_classifications),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load US device classifications", err),
      });

    this.manageAssessmentApiService
      .getEuDeviceClassifications()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: {
          eu_device_classifications: DeviceClassifications[];
        }) => (this.euMedDeviceOptions = response.eu_device_classifications),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load EU device classifications", err),
      });

    this.manageAssessmentApiService
      .getProductDesignHistoryDoc()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: {
          product_design_history_docs: ProductDesignHistory[];
        }) =>
        (this.productDesignHistoryOptions =
          response.product_design_history_docs),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load product design history documents", err),
      });

    this.manageAssessmentApiService
      .getChangeTypes()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { change_types: ChangeTypes[] }) =>
          (this.typeOfChanges = response.change_types),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load type of changes", err),
      });

    this.manageAssessmentApiService
      .getProductFamilies()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: { product_families: ProductFamilies[] }) =>
          (this.productFamilies = response.product_families),
        error: (err: HttpErrorResponse) =>
          console.error("Failed to load product families", err),
      });
  }

  initializeForm(): void {
    this.rcaForm = this.fb.group({
      title: ["",Validators.required],
      region: [[], Validators.required],
      usMedDeviceClass: [null, Validators.required],
      productDesignHistoryDocumentation: [[], Validators.required],
      otherDesignDocs: [""], // New field for "Other Design Docs"
      country: [[], Validators.required],
      usClassification: [null, Validators.required],
      changeDescription: ["", Validators.required],
      ecrnEcnNumber: [""], // Optional field
      euMedDeviceClass: [null, Validators.required],
      plannedTimeframeForImplementation: ["", Validators.required],
      typeOfChange: [[], Validators.required],
      euClassification: [null, Validators.required],
      additionalAttachmentsDescription: [""],
      productFamily: [[], Validators.required],
      issuedDate: [null, Validators.required],
      catalogueNumber: ["", Validators.required],
      dueDate: [null, Validators.required],
      attachmentUrls: [[]],
      // New fields from response form - these will be patched as strings for display
      deviceClassification: [""],
      supportDocument: [""], // This will hold the concatenated documentation names
      deviceAdditionalAttachmentsDesc: [""], // This is the description for the response attachment
      impactConclusion: [""],
      timeSubmissions: [""],
      impactAssessJustify: [""],
      timeApprove: [""],
      productAuthority: [""],
      productNotify: [""],
      timeProductNotify: [""],
      otherDocumentation: [""], // For the 'Other(s)' text
    });

    // Set up tracking for "Other(s)" selection
    this.setupOtherDesignDocsTracking();
  }

  setupOtherDesignDocsTracking(): void {
    const productDesignHistoryControl = this.rcaForm.get('productDesignHistoryDocumentation');

    // For the detail popup, we only need to check visibility, no validation changes since it's read-only
    productDesignHistoryControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedOptions: ProductDesignHistory[] | null) => {
        // Check if "Other(s)" is selected
        const hasOtherSelected = selectedOptions?.some(option =>
          option.name?.toLowerCase().includes('other')
        ) || false;

        this.showOtherDesignDocsField = hasOtherSelected;
      });

    // Trigger initial check in case form already has values
    const currentSelection = productDesignHistoryControl?.value;
    if (currentSelection) {
      const hasOtherSelected = currentSelection.some((option: ProductDesignHistory) =>
        option.name?.toLowerCase().includes('other')
      );
      this.showOtherDesignDocsField = hasOtherSelected;
    }
  }

  // This method now receives the full combined RCA and Response data
  patchFormWithRcaData(data: any) {
    // console.log("Patching form with combined RCA and Response data:", data);
    if (!data) {
      console.warn("No data provided for patching.");
      return;
    }

    const findByName = (arr: any[], name: string) =>
      arr.find((item) => item.name === name);
    const findById = (arr: any[], id: number | boolean) =>
      arr.find((item) => item.id === id);

    // --- Patching original RCA fields ---
    this.rcaForm.patchValue({
      title: data.title || "",
      usMedDeviceClass: findByName(this.usMedDeviceOptions, data.us_med_class_id),
      usClassification: findByName(this.usClassifications, data.us_class_status),
      euMedDeviceClass: findByName(this.euMedDeviceOptions, data.eu_med_class_id),
      euClassification: findByName(this.euClassifications, data.eu_class_status),
      ecrnEcnNumber: data.ecn_ern,
      catalogueNumber: data.catalogue_no,
      changeDescription: data.description,
      plannedTimeframeForImplementation: data.planned_timeline,
      issuedDate: data.issue_date ? new Date(data.issue_date) : null,
      dueDate: data.due_date ? new Date(data.due_date) : null,
      additionalAttachmentsDescription: data.attachment_description || "",
    });

    // Patch MultiSelects for RCA fields
    if (data.regions && this.regions.length > 0) {
      const selectedRegions = data.regions
        .map((regionName: string) => findByName(this.regions, regionName))
        .filter(Boolean);
      this.rcaForm.get("region")?.patchValue(selectedRegions);
      this.cdr.detectChanges();
      if (selectedRegions.length > 0) {
        const selectedRegionIds = selectedRegions.map((region: any) => region.id);
        this.countries = this.allCountries.filter((country) =>
          selectedRegionIds.includes(country.region.id)
        );
        this.rcaForm.get("country")?.enable();
      } else {
        this.countries = [];
        this.rcaForm.get("country")?.disable();
      }
    } else {
      this.rcaForm.get("region")?.patchValue([]);
      this.countries = [];
      this.rcaForm.get("country")?.disable();
    }

    setTimeout(() => {
      if (data.countries && this.allCountries.length > 0) {
        const selectedCountries = data.countries
          .map((countryName: string) => findByName(this.allCountries, countryName))
          .filter(Boolean);
        this.rcaForm.get("country")?.patchValue(selectedCountries);
        this.cdr.detectChanges();
      } else {
        this.rcaForm.get("country")?.patchValue([]);
      }
    }, 100);

    if (data.change_types && this.typeOfChanges.length > 0) {
      const selectedChangeTypes = data.change_types
        .map((typeName: string) => findByName(this.typeOfChanges, typeName))
        .filter(Boolean);
      this.rcaForm.get("typeOfChange")?.patchValue(selectedChangeTypes);
    }
    if (data.product_families && this.productFamilies.length > 0) {
      const selectedProductFamilies = data.product_families
        .map((familyName: string) => findByName(this.productFamilies, familyName))
        .filter(Boolean);
      this.rcaForm.get("productFamily")?.patchValue(selectedProductFamilies);
    }
    if (data.design_history_docs && this.productDesignHistoryOptions.length > 0) {
      const selectedDesignHistoryDocs = data.design_history_docs
        .map((docName: string) => findByName(this.productDesignHistoryOptions, docName))
        .filter(Boolean);
      this.rcaForm.get("productDesignHistoryDocumentation")?.patchValue(selectedDesignHistoryDocs);

      // Trigger the "Other" field visibility check after patching
      const hasOtherSelected = selectedDesignHistoryDocs.some((doc: ProductDesignHistory) =>
        doc.name?.toLowerCase().includes('other')
      );
      this.showOtherDesignDocsField = hasOtherSelected;
    }

    // Handle other design docs if available from API response
    if (data.other_design_docs) {
      this.rcaForm.get("otherDesignDocs")?.patchValue(data.other_design_docs);
    }

    // --- Patching new fields from Response Form ---
    const deviceClassificationName = findById(this.deviceClassificationsOptions, data.device_class)?.name || '';
    const impactConclusionName = findById(this.impactConclusionOptions, data.impact_conclusion)?.name || '';
    const productAuthorityName = findById(this.yesNoOptions, data.prior_approval_acceptance)?.name || '';
    const productNotifyName = findById(this.yesNoOptions, data.post_approval_acceptance)?.name || '';

    // Handle documentation_required (multi-select)
    let documentationRequiredDisplay = '';
    if (data.documentation_required && Array.isArray(data.documentation_required)) {
      const selectedDocNames = data.documentation_required
        .map((docId: any) => findById(this.documentationOptions, docId)?.name)
        .filter(Boolean); // Filter out undefined/null
      documentationRequiredDisplay = selectedDocNames.join(', ');
    }

    this.rcaForm.patchValue({
      deviceClassification: deviceClassificationName,
      supportDocument: documentationRequiredDisplay, // Display concatenated names
      deviceAdditionalAttachmentsDesc: data.response_content || "", // This maps to additionalAttachments in next-form
      impactConclusion: impactConclusionName,
      timeSubmissions: (data.local_authority_eta !== undefined && data.local_authority_eta !== null && data.local_authority_eta !== 0) ? String(data.local_authority_eta) : "",
      impactAssessJustify: data.impact_assess_justification || "",
      timeApprove: (data.health_authority_eta !== undefined && data.health_authority_eta !== null && data.health_authority_eta !== 0) ? String(data.health_authority_eta) : "",
      productAuthority: productAuthorityName,
      productNotify: productNotifyName,
      timeProductNotify: data.time_product_notify || "",
      otherDocumentation: data.other_documentation || "", // Patch the 'Other(s)' text
    });

    // Patch the uploaded file details - handle RA Admin and Local RA attachments separately
    const mapToUploadedFiles = (attachments: any[] | undefined | null): UploadedFile[] =>
      Array.isArray(attachments)
        ? attachments
          .map((attachment: any) => this.mapAttachmentToUploadedFile(attachment))
          .filter((file: UploadedFile | null): file is UploadedFile => file !== null)
        : [];

    this.raAdminUploadedFiles = mapToUploadedFiles(
      data.rcaAttachments ?? data.attachments
    );
    this.responseUploadedFiles = mapToUploadedFiles(data.responseAttachments);

    this.rcaForm.disable(); // Make the entire form read-only
    this.rcaPatched.emit();
    this.cdr.detectChanges();
  }

  private mapAttachmentToUploadedFile(attachment: any): UploadedFile | null {
    if (!attachment) {
      return null;
    }

    const normalizedStatus = (attachment.status || '').toString().toLowerCase();
    const hasValidId = attachment.id !== undefined && attachment.id !== null && `${attachment.id}`.trim() !== '';

    let fileName: string | null = attachment.file_name || null;
    if (!fileName && attachment.file_path) {
      fileName = attachment.file_path.split('/').pop() || null;
    }
    if (!fileName && hasValidId) {
      fileName = `Attachment-${attachment.id}`;
    }

    const directUrl = attachment.url || attachment.file_url || null;
    let downloadUrl = directUrl;
    if (!downloadUrl && hasValidId) {
      downloadUrl = `/api/attachments/download/${attachment.id}/`;
    }

    return {
      name: fileName || 'Attachment',
      url: downloadUrl || '',
      id: hasValidId ? Number(attachment.id) : undefined,
      path: attachment.file_path,
      status: normalizedStatus || undefined,
      uploadedAt: attachment.uploaded_at,
    };
  }

  // Method to handle file download
  async downloadFile(file: UploadedFile) {
    const fileStatus = (file.status || '').toLowerCase();

    if (fileStatus === "failed") {
      this.messageService.add({
        severity: "error",
        summary: "Download Failed",
        detail: "This file upload failed and cannot be downloaded.",
        life: 5000,
      });
      return;
    }

    if (fileStatus === "pending") {
      this.messageService.add({
        severity: "warn",
        summary: "File Pending",
        detail: "This file is still pending verification. Please try again later.",
        life: 5000,
      });
      return;
    }

    if (fileStatus === "scanning") {
      this.messageService.add({
        severity: "warn",
        summary: "File status: Scanning",
        detail: "This file is still being processed. Please try again later.",
        life: 5000,
      });
      return;
    }

    if (!file.url) {
      this.messageService.add({
        severity: "warn",
        summary: "Download Unavailable",
        detail: "No download link is available for this attachment yet.",
        life: 4000,
      });
      return;
    }

    const isApiDownloadUrl = file.url.includes("/api/attachments/download/");
    if (isApiDownloadUrl) {
      try {
        const res: any = await firstValueFrom(
          this.apiService.getSignedAttachmentDownloadFromUrl(file.url)
        );
        if (res?.download_url) {
          await this.triggerFileDownload(res.download_url, file.name);
          this.messageService.add({
            severity: "success",
            summary: "Download Started",
            detail: "Your download should begin shortly.",
            life: 3000,
          });
        } else {
          this.messageService.add({
            severity: "error",
            summary: "Download Error",
            detail: "Failed to get a valid download link from the server.",
            life: 5000,
          });
        }
      } catch (err: any) {
        console.error("Failed to get signed attachment URL:", err);
        this.messageService.add({
          severity: "error",
          summary: "Download Failed",
          detail:
            err.error?.message || "Unable to download file. Please try again.",
          life: 5000,
        });
      }
    } else {
      // For direct file URLs (e.g., from Firebase Storage directly)
      try {
        await this.triggerFileDownload(file.url, file.name);
        this.messageService.add({
          severity: "success",
          summary: "Download Started",
          detail: "Your download should begin shortly.",
          life: 3000,
        });
      } catch (err: any) {
        console.error("Failed to download file directly:", err);
        this.messageService.add({
          severity: "error",
          summary: "Download Failed",
          detail: "Unable to download file. Please try again.",
          life: 5000,
        });
      }
    }
  }

  private async triggerFileDownload(url: string, fileName = 'download'): Promise<void> {
    try {
      // Fetch the file as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        link.remove();
        globalThis.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to direct link method if fetch fails
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
      }, 100);
    }
  }

  // Helper method to get status display information for each attachment
  getFileStatusInfo(file: UploadedFile): { icon: string; class: string; text: string; canDownload: boolean } {
    const status = (file.status || '').toLowerCase();

    switch (status) {
      case 'success':
      case 'passed':
        return {
          icon: 'pi-check-circle',
          class: 'text-green-600',
          text: 'Upload successful',
          canDownload: true,
        };
      case 'pending':
        return {
          icon: 'pi-clock',
          class: 'text-yellow-600',
          text: 'Pending...',
          canDownload: false,
        };
      case 'scanning':
        return {
          icon: 'pi-clock',
          class: 'text-yellow-600',
          text: 'Scanning...',
          canDownload: false,
        };
      case 'failed':
        return {
          icon: 'pi-times-circle',
          class: 'text-red-600',
          text: 'Upload failed',
          canDownload: false,
        };
      default:
        return {
          icon: file.url ? 'pi-check-circle' : 'pi-question-circle',
          class: file.url ? 'text-green-600' : 'text-gray-600',
          text: file.url ? 'Available for download' : 'Attachment details unavailable',
          canDownload: !!file.url,
        };
    }
  }

  openReadonlyFullscreen(field: ReadonlyFullscreenField): void {
    this.readonlyExpanded = field;
  }

  close(): void {
    this.readonlyExpanded = null;
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.closeDialog.emit();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Check if text length exceeds a certain threshold to show tooltip
   * @param text The text to check
   * @param maxLength Maximum length before showing tooltip (default 30)
   */
  shouldShowTooltip(text: string | undefined, maxLength: number = 20): boolean {
    return text ? text.length > maxLength : false;
  }
}